import React, { useEffect, useMemo, useState } from 'react';
import PanelHelpBadge from '../PanelHelpBadge';
import {
  createSavedSegmentQuery,
  deleteSavedSegmentQuery,
  fetchSegmentAnalysisDictionaryOptions,
  fetchSegmentAnalysisDimensions,
  fetchSavedSegmentQueries,
  fetchSegmentAnalysisQuery,
  updateSavedSegmentQuery
} from '../../../lib/admin/segmentAnalysisClient';

const CORE_AXES = [
  'Bebekli',
  'Alerjanlı',
  'Diyet',
  'Sporcu',
  'Sağlık+',
  'Premium',
  'Plan Yapan',
  'Market Listesi Kullanan',
  'Favori Davranışı Yüksek',
  'Kurtarma Ürünü Kullanan',
  'Özel Gün Yapan',
  'Yoğun Çalışan'
];

const METRIC_GROUPS = [
  {
    key: 'davranis',
    label: 'Davranış Metrikleri',
    metrics: [
      { key: 'kullaniciSayisi', label: 'Kullanıcı Sayısı' },
      { key: 'kabulOrani', label: 'Kabul Oranı' },
      { key: 'wowOrani', label: 'Wow Oranı' },
      { key: 'planUyumu', label: 'Plan Uyumu' }
    ]
  },
  {
    key: 'ekonomi',
    label: 'Ekonomi Metrikleri',
    metrics: [
      { key: 'alisverisDonusum', label: 'Alışverişe Dönüşüm' }
    ]
  },
  {
    key: 'saglik',
    label: 'Sağlık Metrikleri',
    metrics: [
      { key: 'alerjenOrani', label: 'Alerjen Maruziyet Oranı' }
    ]
  }
];

const BREAKDOWN_OPTIONS = [
  { key: 'yemekTipi', label: 'Yemek Tipi' },
  { key: 'alerjenTuru', label: 'Alerjen Türü' },
  { key: 'premiumDagilimi', label: 'Premium/Free' }
];

const HEDEF_IZIN_SECENEKLERI = [
  { key: 'dashboard.operations.view', label: 'Operasyon Görüntüleme' },
  { key: 'dashboard.economy.view', label: 'Ekonomi Görüntüleme' },
  { key: 'dashboard.expert.view', label: 'Uzman Görüntüleme' },
  { key: 'dashboard.actions.manage', label: 'Aksiyon Yönetimi' }
];

const FIELD_OPTIONS = [
  {
    key: 'alerjen',
    label: 'Alerjen',
    type: 'text',
    operators: [{ key: 'icerir', label: 'İçerir' }]
  },
  {
    key: 'premium',
    label: 'Premium',
    type: 'boolean',
    operators: [{ key: 'esittir', label: 'Eşittir' }],
    values: [
      { key: 'true', label: 'Evet' },
      { key: 'false', label: 'Hayır' }
    ]
  },
  {
    key: 'planOlustu',
    label: 'Plan Oluştu',
    type: 'boolean',
    operators: [{ key: 'esittir', label: 'Eşittir' }],
    values: [
      { key: 'true', label: 'Evet' },
      { key: 'false', label: 'Hayır' }
    ]
  }
];

function dimensionToFieldOption(dimension) {
  return {
    key: dimension.id,
    label: dimension.label,
    type: dimension.type,
    domain: dimension.domain,
    operators: Array.isArray(dimension.operators) && dimension.operators.length > 0
      ? dimension.operators
      : [{ key: 'esittir', label: 'Eşittir' }],
    values: dimension.values
  };
}

function formatPct(value) {
  return `${Number(value || 0).toLocaleString('tr-TR')}%`;
}

function formatNum(value) {
  return Number(value || 0).toLocaleString('tr-TR');
}

function buildDefaultFilterRow() {
  return {
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    alan: 'allergy',
    islem: 'icerir',
    deger: ''
  };
}

function buildFilterRowFromDimension(dimension) {
  const operator = dimension?.operators?.[0]?.key || 'esittir';
  return {
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    alan: dimension?.key || 'allergy',
    islem: operator,
    deger: dimension?.type === 'boolean' ? 'true' : ''
  };
}

function normalizeFilterRowsForPayload(rows = []) {
  return rows
    .map((row) => ({
      alan: row.alan,
      islem: row.islem,
      deger: row.deger === 'true' ? true : row.deger === 'false' ? false : row.deger
    }))
    .filter((row) => String(row.deger ?? '').trim() !== '');
}

function parseTargetUserIds(text) {
  return Array.from(
    new Set(
      String(text || '')
        .split(',')
        .map((x) => Number(String(x || '').trim()))
        .filter((x) => Number.isFinite(x) && x > 0)
    )
  ).slice(0, 50);
}

function toDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function csvEscape(value) {
  const text = String(value ?? '');
  const escaped = text.replaceAll('"', '""');
  return `"${escaped}"`;
}

function uyariTurEtiketi(tur) {
  const key = String(tur || '').toLocaleLowerCase('tr-TR');
  if (key === 'dusuk_orneklem') return 'düşük örneklem';
  if (key === 'veri_eksik') return 'veri eksik';
  if (key === 'olay_tutarsiz') return 'olay tutarsız';
  if (key === 'aykiri_deger') return 'aykırı değer';
  return 'genel uyarı';
}

function domainLabel(domain) {
  const key = String(domain || '').toLocaleLowerCase('tr-TR');
  if (key === 'profil') return 'Profil';
  if (key === 'ekonomi') return 'Ekonomi';
  if (key === 'davranis') return 'Davranış';
  if (key === 'stok') return 'Stok';
  if (key === 'tuketim') return 'Tüketim';
  if (key === 'segment') return 'Segment';
  return 'Genel';
}

function isDictionaryField(field) {
  return field?.valueResolver === 'dictionary' || field?.type === 'dictionary_ref' || field?.type === 'dictionary_ref_days';
}

function DictionaryValueInput({ field, value, onChange }) {
  const [query, setQuery] = useState(String(value || ''));
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setQuery(String(value || ''));
  }, [value]);

  useEffect(() => {
    let active = true;
    const q = String(query || '').trim();
    if (!isDictionaryField(field) || q.length < 2) {
      setOptions([]);
      setLoading(false);
      return () => {
        active = false;
      };
    }

    setLoading(true);
    const timer = window.setTimeout(async () => {
      try {
        const json = await fetchSegmentAnalysisDictionaryOptions(q);
        if (!active) return;
        setOptions(Array.isArray(json?.items) ? json.items : []);
      } catch (_) {
        if (active) setOptions([]);
      } finally {
        if (active) setLoading(false);
      }
    }, 180);

    return () => {
      active = false;
      window.clearTimeout(timer);
    };
  }, [field, query]);

  return (
    <div className="md:col-span-4">
      <input
        className="w-full rounded-md border border-slate-200 bg-white px-2 py-1 text-xs dark:border-slate-700 dark:bg-slate-900"
        placeholder="Ürün adı veya dictionary_id"
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          onChange(e.target.value);
        }}
      />
      {loading ? (
        <p className="mt-1 text-[10px] font-semibold text-slate-400">Aranıyor...</p>
      ) : null}
      {options.length > 0 ? (
        <div className="mt-1 max-h-32 overflow-y-auto rounded-md border border-slate-200 bg-white p-1 shadow-sm dark:border-slate-700 dark:bg-slate-900">
          {options.map((item) => (
            <button
              key={item.id}
              type="button"
              className="block w-full rounded px-2 py-1 text-left text-[11px] hover:bg-slate-50 dark:hover:bg-slate-800"
              onClick={() => {
                setQuery(item.id);
                onChange(item.id);
                setOptions([]);
              }}
            >
              <span className="block font-bold text-slate-700 dark:text-slate-200">{item.label}</span>
              <span className="block text-slate-400">{item.id} · {item.category}</span>
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function normalizeFieldKeyForUi(value) {
  const key = String(value || '').trim();
  const lower = key.toLocaleLowerCase('tr-TR');
  if (lower === 'alerjen') return 'allergy';
  if (lower === 'planolustu') return 'planOlustu';
  if (lower === 'hasbaby') return 'hasBaby';
  if (lower === 'billingtype') return 'billingType';
  return key;
}

export default function SegmentAnalysisWorkbench({
  segmentCards = [],
  snapshotData = {},
  decisionFeedback = null,
  filters = {},
  onOpenDrawer
}) {
  const [selectedAxis, setSelectedAxis] = useState('Bebekli');
  const [rootJoiner, setRootJoiner] = useState('AND');
  const [compareEnabled, setCompareEnabled] = useState(true);
  const [queryLoading, setQueryLoading] = useState(false);
  const [queryError, setQueryError] = useState('');
  const [queryData, setQueryData] = useState(null);
  const [filterRows, setFilterRows] = useState([]);
  const [conditionGroups, setConditionGroups] = useState([]);
  const [selectedMetrics, setSelectedMetrics] = useState([
    'kullaniciSayisi',
    'kabulOrani',
    'wowOrani',
    'planUyumu',
    'alerjenOrani',
    'alisverisDonusum'
  ]);
  const [selectedBreakdowns, setSelectedBreakdowns] = useState(['yemekTipi', 'alerjenTuru', 'premiumDagilimi']);
  const [savedQueries, setSavedQueries] = useState([]);
  const [queryName, setQueryName] = useState('');
  const [queryVisibility, setQueryVisibility] = useState('ekip');
  const [targetPermissionKeys, setTargetPermissionKeys] = useState([]);
  const [targetUserIdsInput, setTargetUserIdsInput] = useState('');
  const [varsayilanYuklendi, setVarsayilanYuklendi] = useState(false);
  const [guideModalOpen, setGuideModalOpen] = useState(false);
  const [savedFilterMode, setSavedFilterMode] = useState('tum');
  const [savedVisibilityMode, setSavedVisibilityMode] = useState('tum');
  const [sayfa, setSayfa] = useState(1);
  const [sayfaBoyutu, setSayfaBoyutu] = useState(20);
  const [dimensionOptions, setDimensionOptions] = useState(FIELD_OPTIONS);

  const cards = Array.isArray(segmentCards) ? segmentCards : [];
  const selectedCard = useMemo(() => {
    const normalized = String(selectedAxis || '').toLocaleLowerCase('tr-TR');
    return cards.find((c) => String(c?.name || '').toLocaleLowerCase('tr-TR') === normalized) || cards[0] || null;
  }, [cards, selectedAxis]);
  const fieldOptions = dimensionOptions.length > 0 ? dimensionOptions : FIELD_OPTIONS;
  const fieldOptionsByDomain = useMemo(() => {
    const map = new Map();
    for (const option of fieldOptions) {
      const domain = option.domain || 'genel';
      if (!map.has(domain)) map.set(domain, []);
      map.get(domain).push(option);
    }
    return Array.from(map.entries()).map(([domain, items]) => ({ domain, items }));
  }, [fieldOptions]);

  const summaryRows = useMemo(() => {
    const apiRows = Array.isArray(queryData?.ozetMetrikler) ? queryData.ozetMetrikler : [];
    if (apiRows.length > 0) {
      return apiRows.map((row) => ({
        metric: row?.etiket || row?.anahtar || 'Metrik',
        value: typeof row?.deger === 'number' ? Number(row.deger).toLocaleString('tr-TR') : String(row?.deger ?? '-')
      }));
    }
    return [];
  }, [queryData?.ozetMetrikler]);

  const kitchenBreakdown = useMemo(() => {
    const rows = Array.isArray(queryData?.dagilimlar?.yemekTipi) ? queryData.dagilimlar.yemekTipi : [];
    return rows.map((row) => ({
      label: row?.etiket || 'Diğer',
      value: formatNum(row?.deger || 0)
    }));
  }, [queryData?.dagilimlar?.yemekTipi]);

  const compareRows = useMemo(() => {
    const apiRows = Array.isArray(queryData?.kiyasSonucu) ? queryData.kiyasSonucu : [];
    if (compareEnabled && apiRows.length > 0) {
      return apiRows.map((row) => ({
        metric: row?.metrik || 'Metrik',
        segment: typeof row?.segment === 'number' ? Number(row.segment).toLocaleString('tr-TR') : String(row?.segment ?? '-'),
        general: typeof row?.genel === 'number' ? Number(row.genel).toLocaleString('tr-TR') : String(row?.genel ?? '-'),
        delta: typeof row?.fark === 'number' ? Number(row.fark).toLocaleString('tr-TR') : String(row?.fark ?? '-')
      }));
    }
    return [];
  }, [queryData?.kiyasSonucu, compareEnabled]);

  const resultRows = useMemo(() => (Array.isArray(queryData?.sonucTablosu) ? queryData.sonucTablosu : []), [queryData?.sonucTablosu]);
  const sayfalama = useMemo(() => {
    const raw = queryData?.meta?.sayfalama || {};
    const toplamSayfa = Math.max(1, Number(raw.toplamSayfa || 1));
    const aktifSayfa = Math.min(toplamSayfa, Math.max(1, Number(raw.sayfa || sayfa || 1)));
    return {
      sayfa: aktifSayfa,
      sayfaBoyutu: Number(raw.sayfaBoyutu || sayfaBoyutu || 20),
      toplamSayfa,
      sonucToplam: Number(raw.sonucToplam || resultRows.length || 0),
      kiyasToplam: Number(raw.kiyasToplam || compareRows.length || 0)
    };
  }, [queryData?.meta?.sayfalama, sayfa, sayfaBoyutu, resultRows.length, compareRows.length]);

  const normalizedFilters = useMemo(
    () => normalizeFilterRowsForPayload(filterRows),
    [filterRows]
  );
  const normalizedConditionGroups = useMemo(
    () =>
      conditionGroups
        .map((group) => ({
          ...group,
          rows: normalizeFilterRowsForPayload(group.rows || [])
        }))
        .filter((group) => group.rows.length > 0),
    [conditionGroups]
  );

  const currentPayload = useMemo(() => {
    const segmentKey = cards.find(
      (c) => String(c?.name || '').toLocaleLowerCase('tr-TR') === String(selectedAxis || '').toLocaleLowerCase('tr-TR')
    )?.key || selectedAxis.toLocaleLowerCase('tr-TR');
    const conditionChildren = [
      { tip: 'chip', alan: 'segment', islem: 'esittir', deger: segmentKey },
      ...normalizedFilters.map((row) => ({
        tip: 'chip',
        alan: row.alan,
        islem: row.islem,
        deger: row.deger
      })),
      ...normalizedConditionGroups.map((group) => ({
        tip: 'group',
        birlestirici: group.joiner === 'OR' ? 'OR' : 'AND',
        cocuklar: group.rows.map((row) => ({
          tip: 'chip',
          alan: row.alan,
          islem: row.islem,
          deger: row.deger
        }))
      }))
    ];
    return {
      segment: [segmentKey],
      filtreler: normalizedFilters,
      kosul: {
        tip: 'group',
        birlestirici: rootJoiner,
        cocuklar: conditionChildren
      },
      tarihAraligi: filters?.window === 'short' ? '7g' : filters?.window === 'long' ? '90g' : '30g',
      metrikler: selectedMetrics,
      kirilim: selectedBreakdowns,
      kiyas: {
        aktif: Boolean(compareEnabled),
        mod: 'segment_vs_genel'
      },
      sayfa,
      sayfaBoyutu
    };
  }, [cards, selectedAxis, normalizedFilters, normalizedConditionGroups, rootJoiner, filters?.window, selectedMetrics, selectedBreakdowns, compareEnabled, sayfa, sayfaBoyutu]);

  const metricGroupByKey = useMemo(() => {
    const map = new Map();
    METRIC_GROUPS.forEach((group) => {
      group.metrics.forEach((metric) => {
        map.set(metric.key, group.key);
      });
    });
    return map;
  }, []);

  const selectedMetricGroups = useMemo(() => {
    const keys = new Set(
      selectedMetrics
        .map((metricKey) => metricGroupByKey.get(metricKey))
        .filter(Boolean)
    );
    return Array.from(keys);
  }, [selectedMetrics, metricGroupByKey]);

  const metricSecimUyarisi = useMemo(() => {
    if (selectedMetricGroups.length >= 3) {
      return 'Çok fazla metrik grubu seçildi. Okuma kalitesi için en fazla 2 grupta kalman önerilir.';
    }
    if (selectedMetrics.length > 8) {
      return 'Metrik sayısı yüksek. Önce çekirdek metrik seti ile başlaman önerilir.';
    }
    return '';
  }, [selectedMetricGroups.length, selectedMetrics.length]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const json = await fetchSegmentAnalysisDimensions();
        if (!mounted) return;
        const dimensions = Array.isArray(json?.dimensions) ? json.dimensions : [];
        const nextOptions = dimensions.map(dimensionToFieldOption).filter((item) => item.key);
        if (nextOptions.length > 0) setDimensionOptions(nextOptions);
      } catch (_) {
        if (mounted) setDimensionOptions(FIELD_OPTIONS);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setQueryLoading(true);
        setQueryError('');
        const json = await fetchSegmentAnalysisQuery(currentPayload);
        if (!mounted) return;
        setQueryData(json || null);
      } catch (_) {
        if (!mounted) return;
        setQueryError('Segment analizi sorgusu alınamadı.');
      } finally {
        if (mounted) setQueryLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [currentPayload]);

  useEffect(() => {
    setSayfa(1);
  }, [selectedAxis, rootJoiner, normalizedFilters, normalizedConditionGroups, selectedMetrics, selectedBreakdowns, compareEnabled, filters?.window, sayfaBoyutu]);

  const toggleMetric = (key) => {
    setSelectedMetrics((prev) => {
      const exists = prev.includes(key);
      if (exists) return prev.filter((x) => x !== key);
      const nextGroups = new Set(
        [...prev, key]
          .map((metricKey) => metricGroupByKey.get(metricKey))
          .filter(Boolean)
      );
      if (nextGroups.size > 2) return prev;
      return [...prev, key];
    });
  };

  const toggleBreakdown = (key) => {
    setSelectedBreakdowns((prev) => {
      const exists = prev.includes(key);
      if (exists) return prev.filter((x) => x !== key);
      return [...prev, key];
    });
  };

  const addFilterRow = () => setFilterRows((prev) => [...prev, buildDefaultFilterRow()]);
  const addFilterFromDimension = (dimension) => {
    if (!dimension?.key) return;
    setFilterRows((prev) => [...prev, buildFilterRowFromDimension(dimension)]);
  };
  const addConditionGroup = () => {
    setConditionGroups((prev) => [
      ...prev,
      {
        id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        joiner: 'OR',
        rows: [buildDefaultFilterRow()]
      }
    ]);
  };
  const removeFilterRow = (id) => setFilterRows((prev) => prev.filter((row) => row.id !== id));

  const updateFilterRow = (id, patch) => {
    setFilterRows((prev) => prev.map((row) => {
      if (row.id !== id) return row;
      const next = { ...row, ...patch };
      if (patch.alan) {
        const field = fieldOptions.find((f) => f.key === patch.alan) || fieldOptions[0] || FIELD_OPTIONS[0];
        next.islem = field.operators[0]?.key || 'esittir';
        next.deger = '';
      }
      return next;
    }));
  };
  const updateGroupJoiner = (groupId, joiner) => {
    setConditionGroups((prev) => prev.map((group) => (
      group.id === groupId ? { ...group, joiner: joiner === 'AND' ? 'AND' : 'OR' } : group
    )));
  };
  const removeConditionGroup = (groupId) => {
    setConditionGroups((prev) => prev.filter((group) => group.id !== groupId));
  };
  const addFilterToGroup = (groupId, dimension = null) => {
    setConditionGroups((prev) => prev.map((group) => (
      group.id === groupId
        ? { ...group, rows: [...(group.rows || []), dimension ? buildFilterRowFromDimension(dimension) : buildDefaultFilterRow()] }
        : group
    )));
  };
  const removeGroupFilterRow = (groupId, rowId) => {
    setConditionGroups((prev) => prev.map((group) => (
      group.id === groupId
        ? { ...group, rows: (group.rows || []).filter((row) => row.id !== rowId) }
        : group
    )));
  };
  const updateGroupFilterRow = (groupId, rowId, patch) => {
    setConditionGroups((prev) => prev.map((group) => {
      if (group.id !== groupId) return group;
      return {
        ...group,
        rows: (group.rows || []).map((row) => {
          if (row.id !== rowId) return row;
          const next = { ...row, ...patch };
          if (patch.alan) {
            const field = fieldOptions.find((f) => f.key === patch.alan) || fieldOptions[0] || FIELD_OPTIONS[0];
            next.islem = field.operators[0]?.key || 'esittir';
            next.deger = '';
          }
          return next;
        })
      };
    }));
  };

  const savedScopeParam = savedFilterMode === 'favori' ? 'favori' : 'tum';

  const refreshSavedQueries = async (scope = savedScopeParam) => {
    try {
      const json = await fetchSavedSegmentQueries({ scope });
      setSavedQueries(Array.isArray(json?.items) ? json.items : []);
    } catch (_) {
      setSavedQueries([]);
    }
  };

  const handleSaveQuery = async () => {
    const name = String(queryName || '').trim();
    if (!name) return;
    const hedefIzinler = queryVisibility === 'ekip'
      ? Array.from(new Set(targetPermissionKeys.map((x) => String(x || '').trim().toLowerCase()).filter(Boolean))).slice(0, 10)
      : [];
    const hedefKullanicilar = queryVisibility === 'ekip' ? parseTargetUserIds(targetUserIdsInput) : [];
    await createSavedSegmentQuery({
      ad: name,
      payload: currentPayload,
      favori: false,
      varsayilan: false,
      paylasimSeviyesi: queryVisibility === 'ozel' ? 'ozel' : 'ekip',
      hedefIzinler,
      hedefKullanicilar
    });
    await refreshSavedQueries(savedScopeParam);
    setQueryName('');
    setQueryVisibility('ekip');
    setTargetPermissionKeys([]);
    setTargetUserIdsInput('');
  };

  const handleApplySavedQuery = (item) => {
    const payload = item?.payload || {};
    const segment = Array.isArray(payload.segment) ? payload.segment[0] : '';
    const card = cards.find((c) => String(c?.key || '').toLowerCase() === String(segment || '').toLowerCase());
    if (card?.name) setSelectedAxis(card.name);
    const joiner = String(payload?.kosul?.birlestirici || '').toUpperCase();
    if (joiner === 'AND' || joiner === 'OR') setRootJoiner(joiner);
    setFilterRows(
      (Array.isArray(payload.filtreler) ? payload.filtreler : []).map((row) => ({
        id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        alan: normalizeFieldKeyForUi(row?.alan || 'allergy'),
        islem: row?.islem || 'icerir',
        deger: typeof row?.deger === 'boolean' ? String(row.deger) : String(row?.deger ?? '')
      }))
    );
    const nestedGroups = Array.isArray(payload?.kosul?.cocuklar)
      ? payload.kosul.cocuklar
          .filter((node) => node?.tip === 'group' && Array.isArray(node.cocuklar))
          .map((node) => ({
            id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
            joiner: node.birlestirici === 'AND' ? 'AND' : 'OR',
            rows: node.cocuklar
              .filter((child) => child?.tip === 'chip' && child.alan !== 'segment')
              .map((child) => ({
                id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
                alan: normalizeFieldKeyForUi(child.alan || 'allergy'),
                islem: child.islem || 'esittir',
                deger: typeof child.deger === 'boolean' ? String(child.deger) : String(child.deger ?? '')
              }))
          }))
          .filter((group) => group.rows.length > 0)
      : [];
    setConditionGroups(nestedGroups);
    setSelectedMetrics(Array.isArray(payload.metrikler) && payload.metrikler.length > 0 ? payload.metrikler : selectedMetrics);
    setSelectedBreakdowns(Array.isArray(payload.kirilim) ? payload.kirilim : selectedBreakdowns);
    setCompareEnabled(Boolean(payload?.kiyas?.aktif));
  };

  const handleDeleteSavedQuery = async (id) => {
    await deleteSavedSegmentQuery(id);
    await refreshSavedQueries(savedScopeParam);
  };

  const handleSetDefaultQuery = async (id) => {
    await updateSavedSegmentQuery(id, { varsayilan: true });
    await refreshSavedQueries(savedScopeParam);
  };

  const handleToggleFavorite = async (id) => {
    const item = savedQueries.find((q) => q.id === id);
    if (!item) return;
    await updateSavedSegmentQuery(id, { favori: !Boolean(item.favori) });
    await refreshSavedQueries(savedScopeParam);
  };

  const handleShareSavedQuery = async (id) => {
    const item = savedQueries.find((q) => q.id === id);
    if (String(item?.paylasimSeviyesi || 'ekip') === 'ozel') return;
    const url = new URL(window.location.href);
    url.searchParams.set('sorgu', String(item?.shareToken || id));
    const text = url.toString();
    try {
      await navigator.clipboard.writeText(text);
    } catch (_) {
      window.prompt('Paylaşılabilir sorgu bağlantısı', text);
    }
  };

  useEffect(() => {
    if (varsayilanYuklendi) return;
    if (!Array.isArray(savedQueries) || savedQueries.length === 0) return;
    const defaultQuery = savedQueries.find((q) => q.varsayilan);
    if (!defaultQuery) {
      setVarsayilanYuklendi(true);
      return;
    }
    handleApplySavedQuery(defaultQuery);
    setVarsayilanYuklendi(true);
  }, [savedQueries, varsayilanYuklendi]);

  useEffect(() => {
    refreshSavedQueries(savedScopeParam);
  }, [savedScopeParam]);

  useEffect(() => {
    const search = new URLSearchParams(window.location.search);
    const queryId = search.get('sorgu');
    if (!queryId) return;
    const found = savedQueries.find((q) => q.id === queryId || q.shareToken === queryId);
    if (found) handleApplySavedQuery(found);
  }, [savedQueries]);

  const savedQueriesVisible = useMemo(() => {
    let rows = savedQueries;
    if (savedFilterMode === 'favori') rows = rows.filter((q) => q.favori);
    if (savedVisibilityMode === 'ozel') rows = rows.filter((q) => String(q.paylasimSeviyesi || 'ekip') === 'ozel');
    if (savedVisibilityMode === 'ekip') rows = rows.filter((q) => String(q.paylasimSeviyesi || 'ekip') === 'ekip');
    return rows;
  }, [savedQueries, savedFilterMode, savedVisibilityMode]);

  const veriKalitesiUyariList = useMemo(() => {
    const hiddenMetrics = Array.isArray(queryData?.meta?.hiddenMetrics) ? queryData.meta.hiddenMetrics : [];
    const hiddenMetricWarnings = hiddenMetrics.map((item, index) => ({
      id: `hidden-metric-${item?.anahtar || index}`,
      tur: 'veri eksik',
      mesaj: `${item?.anahtar || 'metrik'} gizlendi: ${item?.neden || 'gerçek hesap bu yüzeye bağlı değil'}`
    }));
    const detayRows = Array.isArray(queryData?.veriKalitesiDetaylari) ? queryData.veriKalitesiDetaylari : [];
    if (detayRows.length > 0) {
      return [
        ...hiddenMetricWarnings,
        ...detayRows.map((item, index) => ({
        id: String(item?.id || `uyari-${index}`),
        tur: uyariTurEtiketi(item?.tur),
        mesaj: String(item?.mesaj || '')
        }))
      ];
    }
    const rows = Array.isArray(queryData?.veriKalitesiUyarilari) ? queryData.veriKalitesiUyarilari : [];
    return [
      ...hiddenMetricWarnings,
      ...rows.map((item, index) => {
      const text = String(item || '');
      return {
        id: `${index}-${text}`,
        tur: 'genel uyarı',
        mesaj: text
      };
      })
    ];
  }, [queryData?.meta?.hiddenMetrics, queryData?.veriKalitesiDetaylari, queryData?.veriKalitesiUyarilari]);

  const handleExportJson = () => {
    const payload = {
      sorgu: currentPayload,
      sonuc: queryData || null
    };
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json;charset=utf-8' });
    toDownload(blob, `segment-analizi-${Date.now()}.json`);
  };

  const handleExportCsv = () => {
    const rows = Array.isArray(resultRows) && resultRows.length > 0
      ? resultRows.map((x) => ({ metrik: x.metrik, deger: x.deger }))
      : summaryRows.map((x) => ({ metrik: x.metric, deger: x.value }));
    const header = ['Metrik', 'Değer'];
    const lines = [header.map(csvEscape).join(',')];
    for (const row of rows) {
      lines.push([csvEscape(row.metrik), csvEscape(row.deger)].join(','));
    }
    if (compareEnabled && compareRows.length > 0) {
      lines.push('');
      lines.push(['Karşılaştırma', 'Segment', 'Genel', 'Fark'].map(csvEscape).join(','));
      for (const row of compareRows) {
        lines.push([csvEscape(row.metric), csvEscape(row.segment), csvEscape(row.general), csvEscape(row.delta)].join(','));
      }
    }
    const csvContent = `\uFEFF${lines.join('\r\n')}`;
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8' });
    toDownload(blob, `segment-analizi-${Date.now()}.csv`);
  };

  return (
    <section className="space-y-4 rounded-xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-[#101922]">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-black text-slate-900 dark:text-white">Segment Analizi</h3>
          <PanelHelpBadge
            title="Segment Analizi"
            description="Segment + filtre + metrik + kırılım kombinasyonu ile ham veri okunur."
            interpretation="Bu yüzeyde otomatik eylem zorunlu değildir; önce veri okunur."
            metricExample="Bebekli + Alerjanlı + son 30 gün sorgusunda kabul ve plan uyumu birlikte izlenir."
          />
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setGuideModalOpen(true)}
            className="rounded-md border border-slate-200 px-2 py-1 text-xs font-semibold text-slate-600 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
          >
            Sistem Nasıl Çalışır?
          </button>
          <button
            type="button"
            onClick={handleExportCsv}
            className="rounded-md border border-slate-200 px-2 py-1 text-xs font-semibold text-slate-600 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
          >
            CSV Dışa Aktar
          </button>
          <button
            type="button"
            onClick={handleExportJson}
            className="rounded-md border border-slate-200 px-2 py-1 text-xs font-semibold text-slate-600 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
          >
            JSON Dışa Aktar
          </button>
          <button
            type="button"
            onClick={() => onOpenDrawer?.('segment-builder', { source: 'segment-analysis' })}
            className="rounded-md border border-slate-200 px-2 py-1 text-xs font-semibold text-slate-600 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
          >
            Sorguyu Derinleştir
          </button>
        </div>
      </div>

      <section className="rounded-lg border border-slate-200 bg-slate-50 p-3 dark:border-slate-700 dark:bg-slate-900/50">
        <p className="text-xs font-semibold text-slate-500 dark:text-slate-400">Segment Eksenleri</p>
        <div className="mt-2 flex flex-wrap gap-2">
          {CORE_AXES.map((axis) => {
            const active = selectedAxis === axis;
            return (
              <button
                key={axis}
                type="button"
                onClick={() => setSelectedAxis(axis)}
                className={`rounded-full border px-3 py-1 text-xs font-semibold ${
                  active
                    ? 'border-primary bg-primary text-white'
                    : 'border-slate-300 text-slate-600 hover:bg-slate-100 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800'
                }`}
              >
                {axis}
              </button>
            );
          })}
        </div>
      </section>

      <section className="rounded-lg border border-slate-200 bg-white p-3 dark:border-slate-800 dark:bg-slate-900/60">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <p className="text-xs font-semibold text-slate-500 dark:text-slate-400">Sorgu Oluşturucu</p>
            <p className="mt-0.5 text-[11px] text-slate-500 dark:text-slate-400">
              Seçili segment ve aşağıdaki chip'ler aynı boolean ağacında değerlendirilir.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex rounded-md border border-slate-200 bg-slate-50 p-0.5 dark:border-slate-700 dark:bg-slate-950">
              {['AND', 'OR'].map((joiner) => (
                <button
                  key={joiner}
                  type="button"
                  onClick={() => setRootJoiner(joiner)}
                  className={`rounded px-2 py-1 text-[11px] font-black ${
                    rootJoiner === joiner
                      ? 'bg-primary text-white'
                      : 'text-slate-500 hover:bg-white dark:text-slate-400 dark:hover:bg-slate-800'
                  }`}
                >
                  {joiner}
                </button>
              ))}
            </div>
            <button
              type="button"
              onClick={addFilterRow}
              className="rounded-md border border-slate-200 px-2 py-1 text-xs font-semibold text-slate-600 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
            >
              Filtre Ekle
            </button>
            <button
              type="button"
              onClick={addConditionGroup}
              className="rounded-md border border-primary/30 px-2 py-1 text-xs font-semibold text-primary hover:bg-primary/5"
            >
              Grup Ekle
            </button>
          </div>
        </div>

        <div className="mt-3 rounded-md border border-dashed border-slate-200 bg-slate-50 p-3 dark:border-slate-700 dark:bg-slate-950/40">
          <p className="text-[11px] font-black uppercase tracking-wide text-slate-500 dark:text-slate-400">Chip Paleti</p>
          <div className="mt-2 grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
            {fieldOptionsByDomain.map(({ domain, items }) => (
              <div key={domain}>
                <p className="text-[11px] font-bold text-slate-500 dark:text-slate-400">{domainLabel(domain)}</p>
                <div className="mt-1 flex flex-wrap gap-1.5">
                  {items.map((item) => {
                    const activeCount = filterRows.filter((row) => row.alan === item.key).length;
                    return (
                      <button
                        key={item.key}
                        type="button"
                        onClick={() => addFilterFromDimension(item)}
                        className={`rounded-full border px-2.5 py-1 text-[11px] font-semibold transition ${
                          activeCount > 0
                            ? 'border-primary bg-primary/10 text-primary dark:bg-primary/20'
                            : 'border-slate-300 bg-white text-slate-600 hover:border-primary hover:text-primary dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300'
                        }`}
                        title={item.key}
                      >
                        {item.label}{activeCount > 0 ? ` (${activeCount})` : ''}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-2 space-y-2">
          {filterRows.length === 0 ? (
            <p className="text-xs text-slate-500 dark:text-slate-400">Filtre yok. İstersen sorguya koşul ekleyebilirsin.</p>
          ) : (
            filterRows.map((row) => {
              const field = fieldOptions.find((f) => f.key === row.alan) || fieldOptions[0] || FIELD_OPTIONS[0];
              return (
                <div key={row.id} className="grid grid-cols-1 gap-2 rounded-md border border-slate-200 p-2 md:grid-cols-12 dark:border-slate-700">
                  <select
                    className="md:col-span-4 rounded-md border border-slate-200 bg-white px-2 py-1 text-xs dark:border-slate-700 dark:bg-slate-900"
                    value={row.alan}
                    onChange={(e) => updateFilterRow(row.id, { alan: e.target.value })}
                  >
                    {fieldOptions.map((opt) => (
                      <option key={opt.key} value={opt.key}>{opt.label}</option>
                    ))}
                  </select>
                  <select
                    className="md:col-span-3 rounded-md border border-slate-200 bg-white px-2 py-1 text-xs dark:border-slate-700 dark:bg-slate-900"
                    value={row.islem}
                    onChange={(e) => updateFilterRow(row.id, { islem: e.target.value })}
                  >
                    {field.operators.map((op) => (
                      <option key={op.key} value={op.key}>{op.label}</option>
                    ))}
                  </select>
                  {Array.isArray(field.values) && field.values.length > 0 ? (
                    <select
                      className="md:col-span-4 rounded-md border border-slate-200 bg-white px-2 py-1 text-xs dark:border-slate-700 dark:bg-slate-900"
                      value={String(row.deger)}
                      onChange={(e) => updateFilterRow(row.id, { deger: e.target.value })}
                    >
                      <option value="">Seç</option>
                      {field.values.map((v) => (
                        <option key={v.key} value={v.key}>{v.label}</option>
                      ))}
                    </select>
                  ) : isDictionaryField(field) ? (
                    <DictionaryValueInput
                      field={field}
                      value={row.deger}
                      onChange={(value) => updateFilterRow(row.id, { deger: value })}
                    />
                  ) : (
                    <input
                      className="md:col-span-4 rounded-md border border-slate-200 bg-white px-2 py-1 text-xs dark:border-slate-700 dark:bg-slate-900"
                      placeholder="Değer"
                      value={row.deger}
                      onChange={(e) => updateFilterRow(row.id, { deger: e.target.value })}
                    />
                  )}
                  <button
                    type="button"
                    className="md:col-span-1 rounded-md border border-rose-200 px-2 py-1 text-xs font-semibold text-rose-600 dark:border-rose-700 dark:text-rose-300"
                    onClick={() => removeFilterRow(row.id)}
                  >
                    Sil
                  </button>
                </div>
              );
            })
          )}
        </div>

        {conditionGroups.length > 0 ? (
          <div className="mt-3 space-y-3">
            {conditionGroups.map((group, groupIndex) => (
              <div key={group.id} className="rounded-lg border border-primary/20 bg-primary/5 p-3 dark:border-primary/30 dark:bg-primary/10">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <p className="text-xs font-black text-slate-700 dark:text-slate-200">
                      Parantez Grubu {groupIndex + 1}
                    </p>
                    <p className="text-[11px] text-slate-500 dark:text-slate-400">
                      Bu gruptaki chip'ler kendi içinde {group.joiner} ile birleşir.
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="flex rounded-md border border-slate-200 bg-white p-0.5 dark:border-slate-700 dark:bg-slate-900">
                      {['AND', 'OR'].map((joiner) => (
                        <button
                          key={`${group.id}-${joiner}`}
                          type="button"
                          onClick={() => updateGroupJoiner(group.id, joiner)}
                          className={`rounded px-2 py-1 text-[11px] font-black ${
                            group.joiner === joiner
                              ? 'bg-primary text-white'
                              : 'text-slate-500 hover:bg-slate-50 dark:text-slate-400 dark:hover:bg-slate-800'
                          }`}
                        >
                          {joiner}
                        </button>
                      ))}
                    </div>
                    <button
                      type="button"
                      onClick={() => addFilterToGroup(group.id)}
                      className="rounded-md border border-slate-200 bg-white px-2 py-1 text-xs font-semibold text-slate-600 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300"
                    >
                      Satır Ekle
                    </button>
                    <button
                      type="button"
                      onClick={() => removeConditionGroup(group.id)}
                      className="rounded-md border border-rose-200 bg-white px-2 py-1 text-xs font-semibold text-rose-600 dark:border-rose-700 dark:bg-slate-900 dark:text-rose-300"
                    >
                      Grubu Sil
                    </button>
                  </div>
                </div>

                <div className="mt-3 flex flex-wrap gap-1.5">
                  {fieldOptions.slice(0, 8).map((item) => (
                    <button
                      key={`${group.id}-palette-${item.key}`}
                      type="button"
                      onClick={() => addFilterToGroup(group.id, item)}
                      className="rounded-full border border-slate-300 bg-white px-2 py-1 text-[11px] font-semibold text-slate-600 hover:border-primary hover:text-primary dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300"
                    >
                      + {item.label}
                    </button>
                  ))}
                </div>

                <div className="mt-3 space-y-2">
                  {(group.rows || []).length === 0 ? (
                    <p className="text-xs text-slate-500 dark:text-slate-400">Bu grup boş. Satır veya chip ekle.</p>
                  ) : (
                    (group.rows || []).map((row) => {
                      const field = fieldOptions.find((f) => f.key === row.alan) || fieldOptions[0] || FIELD_OPTIONS[0];
                      return (
                        <div key={row.id} className="grid grid-cols-1 gap-2 rounded-md border border-slate-200 bg-white p-2 md:grid-cols-12 dark:border-slate-700 dark:bg-slate-900">
                          <select
                            className="md:col-span-4 rounded-md border border-slate-200 bg-white px-2 py-1 text-xs dark:border-slate-700 dark:bg-slate-900"
                            value={row.alan}
                            onChange={(e) => updateGroupFilterRow(group.id, row.id, { alan: e.target.value })}
                          >
                            {fieldOptions.map((opt) => (
                              <option key={opt.key} value={opt.key}>{opt.label}</option>
                            ))}
                          </select>
                          <select
                            className="md:col-span-3 rounded-md border border-slate-200 bg-white px-2 py-1 text-xs dark:border-slate-700 dark:bg-slate-900"
                            value={row.islem}
                            onChange={(e) => updateGroupFilterRow(group.id, row.id, { islem: e.target.value })}
                          >
                            {field.operators.map((op) => (
                              <option key={op.key} value={op.key}>{op.label}</option>
                            ))}
                          </select>
                          {Array.isArray(field.values) && field.values.length > 0 ? (
                            <select
                              className="md:col-span-4 rounded-md border border-slate-200 bg-white px-2 py-1 text-xs dark:border-slate-700 dark:bg-slate-900"
                              value={String(row.deger)}
                              onChange={(e) => updateGroupFilterRow(group.id, row.id, { deger: e.target.value })}
                            >
                              <option value="">Seç</option>
                              {field.values.map((v) => (
                                <option key={v.key} value={v.key}>{v.label}</option>
                              ))}
                            </select>
                          ) : isDictionaryField(field) ? (
                            <DictionaryValueInput
                              field={field}
                              value={row.deger}
                              onChange={(value) => updateGroupFilterRow(group.id, row.id, { deger: value })}
                            />
                          ) : (
                            <input
                              className="md:col-span-4 rounded-md border border-slate-200 bg-white px-2 py-1 text-xs dark:border-slate-700 dark:bg-slate-900"
                              placeholder="Değer"
                              value={row.deger}
                              onChange={(e) => updateGroupFilterRow(group.id, row.id, { deger: e.target.value })}
                            />
                          )}
                          <button
                            type="button"
                            className="md:col-span-1 rounded-md border border-rose-200 px-2 py-1 text-xs font-semibold text-rose-600 dark:border-rose-700 dark:text-rose-300"
                            onClick={() => removeGroupFilterRow(group.id, row.id)}
                          >
                            Sil
                          </button>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : null}

        <div className="mt-3 grid grid-cols-1 gap-3 lg:grid-cols-2">
          <div>
            <p className="text-xs font-semibold text-slate-500 dark:text-slate-400">Metrik Grupları</p>
            <div className="mt-2 space-y-2">
              {METRIC_GROUPS.map((group) => (
                <div key={group.key}>
                  <p className="text-[11px] font-bold text-slate-500 dark:text-slate-400">{group.label}</p>
                  <div className="mt-1 flex flex-wrap gap-2">
                    {group.metrics.map((item) => {
                      const active = selectedMetrics.includes(item.key);
                      const metricGroup = metricGroupByKey.get(item.key);
                      const disabled = !active && selectedMetricGroups.length >= 2 && !selectedMetricGroups.includes(metricGroup);
                      return (
                        <button
                          key={`${group.key}-${item.key}`}
                          type="button"
                          disabled={disabled}
                          onClick={() => toggleMetric(item.key)}
                          className={`rounded-full border px-3 py-1 text-xs font-semibold ${
                            active
                              ? 'border-primary bg-primary text-white'
                              : disabled
                                ? 'cursor-not-allowed border-slate-200 text-slate-400 dark:border-slate-700 dark:text-slate-500'
                                : 'border-slate-300 text-slate-600 dark:border-slate-700 dark:text-slate-300'
                          }`}
                        >
                          {item.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
            {metricSecimUyarisi ? (
              <p className="mt-2 text-xs font-semibold text-amber-600 dark:text-amber-300">{metricSecimUyarisi}</p>
            ) : (
              <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
                Akıllı seçim açık: aynı anda en fazla 2 metrik grubu seçilebilir.
              </p>
            )}
          </div>

          <div>
            <p className="text-xs font-semibold text-slate-500 dark:text-slate-400">Kırılım</p>
            <div className="mt-1 flex flex-wrap gap-2">
              {BREAKDOWN_OPTIONS.map((item) => {
                const active = selectedBreakdowns.includes(item.key);
                return (
                  <button
                    key={item.key}
                    type="button"
                    onClick={() => toggleBreakdown(item.key)}
                    className={`rounded-full border px-3 py-1 text-xs font-semibold ${
                      active
                        ? 'border-primary bg-primary text-white'
                        : 'border-slate-300 text-slate-600 dark:border-slate-700 dark:text-slate-300'
                    }`}
                  >
                    {item.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      <section className="rounded-lg border border-slate-200 bg-white p-3 dark:border-slate-800 dark:bg-slate-900/60">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-xs font-semibold text-slate-500 dark:text-slate-400">Kaydedilmiş Sorgular</p>
          <div className="flex items-center gap-2">
            <select
              value={savedFilterMode}
              onChange={(e) => setSavedFilterMode(e.target.value)}
              className="rounded-md border border-slate-200 bg-white px-2 py-1 text-xs dark:border-slate-700 dark:bg-slate-900"
            >
              <option value="tum">Tümü</option>
              <option value="favori">Yalnız Favoriler</option>
            </select>
            <select
              value={savedVisibilityMode}
              onChange={(e) => setSavedVisibilityMode(e.target.value)}
              className="rounded-md border border-slate-200 bg-white px-2 py-1 text-xs dark:border-slate-700 dark:bg-slate-900"
            >
              <option value="tum">Tüm Görünürlükler</option>
              <option value="ekip">Yalnız Ekip</option>
              <option value="ozel">Yalnız Özel</option>
            </select>
            <input
              value={queryName}
              onChange={(e) => setQueryName(e.target.value)}
              placeholder="Sorgu adı"
              className="rounded-md border border-slate-200 bg-white px-2 py-1 text-xs dark:border-slate-700 dark:bg-slate-900"
            />
            <select
              value={queryVisibility}
              onChange={(e) => setQueryVisibility(e.target.value)}
              className="rounded-md border border-slate-200 bg-white px-2 py-1 text-xs dark:border-slate-700 dark:bg-slate-900"
            >
              <option value="ekip">Ekip ile paylaşılabilir</option>
              <option value="ozel">Özel (yalnız bana)</option>
            </select>
            {queryVisibility === 'ekip' ? (
              <>
                <input
                  value={targetUserIdsInput}
                  onChange={(e) => setTargetUserIdsInput(e.target.value)}
                  placeholder="Hedef kullanıcı id (örn: 12,45)"
                  className="rounded-md border border-slate-200 bg-white px-2 py-1 text-xs dark:border-slate-700 dark:bg-slate-900"
                />
                <div className="flex flex-wrap items-center gap-1">
                  {HEDEF_IZIN_SECENEKLERI.map((izin) => {
                    const aktif = targetPermissionKeys.includes(izin.key);
                    return (
                      <button
                        key={izin.key}
                        type="button"
                        onClick={() => setTargetPermissionKeys((prev) => (
                          prev.includes(izin.key) ? prev.filter((x) => x !== izin.key) : [...prev, izin.key]
                        ))}
                        className={`rounded-full border px-2 py-1 text-[11px] font-semibold ${
                          aktif
                            ? 'border-primary bg-primary text-white'
                            : 'border-slate-300 text-slate-600 dark:border-slate-700 dark:text-slate-300'
                        }`}
                        title={izin.key}
                      >
                        {izin.label}
                      </button>
                    );
                  })}
                </div>
              </>
            ) : null}
            <button
              type="button"
              onClick={handleSaveQuery}
              className="rounded-md border border-slate-200 px-2 py-1 text-xs font-semibold text-slate-600 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
            >
              Sorguyu Kaydet
            </button>
          </div>
        </div>
        <p className="mt-2 text-[11px] text-slate-500 dark:text-slate-400">
          Bu panel çekirdek özelliktir: favori ve paylaşılabilir sorgular ekip tekrar kullanımını hızlandırır.
        </p>
        {savedQueriesVisible.length === 0 ? (
          <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">Henüz kayıtlı sorgu yok.</p>
        ) : (
          <ul className="mt-2 space-y-1.5">
            {savedQueriesVisible.map((item) => (
              <li key={item.id} className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-slate-200 px-2 py-1 dark:border-slate-700">
                <div>
                  <p className="text-xs font-semibold text-slate-700 dark:text-slate-200">{item.ad}</p>
                  {item.varsayilan ? (
                    <p className="text-[11px] font-semibold text-emerald-600 dark:text-emerald-300">Varsayılan sorgu</p>
                  ) : null}
                  {item.favori ? (
                    <p className="text-[11px] font-semibold text-amber-600 dark:text-amber-300">Favori sorgu</p>
                  ) : null}
                  <p className="text-[11px] font-semibold text-cyan-700 dark:text-cyan-300">
                    Görünürlük: {String(item.paylasimSeviyesi || 'ekip') === 'ozel' ? 'Özel' : 'Ekip'}
                  </p>
                  {Array.isArray(item.hedefIzinler) && item.hedefIzinler.length > 0 ? (
                    <p className="text-[11px] text-slate-500 dark:text-slate-400">
                      Hedef izinler: {item.hedefIzinler.join(', ')}
                    </p>
                  ) : null}
                  {Array.isArray(item.hedefKullanicilar) && item.hedefKullanicilar.length > 0 ? (
                    <p className="text-[11px] text-slate-500 dark:text-slate-400">
                      Hedef kullanıcılar: {item.hedefKullanicilar.join(', ')}
                    </p>
                  ) : null}
                  <p className="text-[11px] text-slate-500 dark:text-slate-400">{new Date(item.createdAt).toLocaleString('tr-TR')}</p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => handleToggleFavorite(item.id)}
                    className="rounded-md border border-amber-200 px-2 py-1 text-[11px] font-semibold text-amber-600 dark:border-amber-700 dark:text-amber-300"
                  >
                    {item.favori ? 'Favoriden Çıkar' : 'Favori Yap'}
                  </button>
                  <button
                    type="button"
                    onClick={() => handleShareSavedQuery(item.id)}
                    disabled={String(item.paylasimSeviyesi || 'ekip') === 'ozel'}
                    className="rounded-md border border-cyan-200 px-2 py-1 text-[11px] font-semibold text-cyan-700 dark:border-cyan-700 dark:text-cyan-300"
                  >
                    Paylaş
                  </button>
                  <button
                    type="button"
                    onClick={() => handleSetDefaultQuery(item.id)}
                    className="rounded-md border border-emerald-200 px-2 py-1 text-[11px] font-semibold text-emerald-600 dark:border-emerald-700 dark:text-emerald-300"
                  >
                    Varsayılan Yap
                  </button>
                  <button
                    type="button"
                    onClick={() => handleApplySavedQuery(item)}
                    className="rounded-md border border-slate-200 px-2 py-1 text-[11px] font-semibold text-slate-600 dark:border-slate-700 dark:text-slate-300"
                  >
                    Uygula
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDeleteSavedQuery(item.id)}
                    className="rounded-md border border-rose-200 px-2 py-1 text-[11px] font-semibold text-rose-600 dark:border-rose-700 dark:text-rose-300"
                  >
                    Sil
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      {veriKalitesiUyariList.length > 0 ? (
        <section className="rounded-lg border border-amber-200 bg-amber-50 p-3 dark:border-amber-700 dark:bg-amber-900/20">
          <div className="flex items-center justify-between gap-2">
            <p className="text-xs font-semibold text-amber-700 dark:text-amber-300">Veri Kalitesi Uyarıları</p>
            <span className="rounded-full border border-amber-300 px-2 py-0.5 text-[10px] font-bold text-amber-700 dark:border-amber-700 dark:text-amber-300">
              {veriKalitesiUyariList.length} uyarı
            </span>
          </div>
          <div className="mt-2 flex flex-wrap gap-2">
            {[...new Set(veriKalitesiUyariList.map((x) => x.tur))].map((tur) => (
              <span key={tur} className="rounded-full border border-amber-300 bg-white px-2 py-0.5 text-[10px] font-semibold text-amber-700 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-300">
                {tur}
              </span>
            ))}
          </div>
          <ul className="mt-2 space-y-1 text-xs text-amber-700 dark:text-amber-300">
            {veriKalitesiUyariList.map((item) => (
              <li key={item.id}>• {item.mesaj}</li>
            ))}
          </ul>
        </section>
      ) : null}

      <section className="rounded-lg border border-slate-200 bg-white p-3 dark:border-slate-800 dark:bg-slate-900/60">
        <p className="text-xs font-semibold text-slate-500 dark:text-slate-400">Özet Metrikler</p>
        {summaryRows.length > 0 ? (
          <table className="mt-2 w-full text-sm">
            <tbody>
              {summaryRows.map((row) => (
                <tr key={row.metric} className="border-b border-slate-100 dark:border-slate-800">
                  <td className="py-1 text-slate-500 dark:text-slate-400">{row.metric}</td>
                  <td className="py-1 text-right font-bold text-slate-900 dark:text-white">{row.value}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className="mt-3 rounded-md border border-dashed border-slate-200 px-3 py-2 text-xs font-semibold text-slate-500 dark:border-slate-700 dark:text-slate-400">
            Sorgu çalıştırılmadı veya bu kohort için hesaplanmış özet metrik yok.
          </p>
        )}
      </section>

      <section className="rounded-lg border border-slate-200 bg-white p-3 dark:border-slate-800 dark:bg-slate-900/60">
        <div className="flex items-center justify-between gap-2">
          <p className="text-xs font-semibold text-slate-500 dark:text-slate-400">Karşılaştırma (Segment vs Genel)</p>
          <div className="flex items-center gap-2">
            <select
              value={sayfaBoyutu}
              onChange={(e) => setSayfaBoyutu(Number(e.target.value || 20))}
              className="rounded-md border border-slate-200 bg-white px-2 py-1 text-xs dark:border-slate-700 dark:bg-slate-900"
            >
              <option value={10}>10 satır</option>
              <option value={20}>20 satır</option>
              <option value={50}>50 satır</option>
            </select>
            <button
              type="button"
              onClick={() => setCompareEnabled((prev) => !prev)}
              className="rounded-md border border-slate-200 px-2 py-1 text-xs font-semibold text-slate-600 dark:border-slate-700 dark:text-slate-300"
            >
              {compareEnabled ? 'Kapat' : 'Aç'}
            </button>
          </div>
        </div>
        {compareRows.length > 0 ? (
          <div className="mt-2 overflow-x-auto">
            <table className="w-full min-w-[520px] text-sm">
              <thead>
                <tr className="text-left text-slate-500 dark:text-slate-400">
                  <th className="py-1">Metrik</th>
                  <th className="py-1">Segment</th>
                  <th className="py-1">Genel</th>
                  <th className="py-1">Fark</th>
                </tr>
              </thead>
              <tbody>
                {compareRows.map((row) => (
                  <tr key={row.metric} className="border-t border-slate-100 dark:border-slate-800">
                    <td className="py-1 text-slate-700 dark:text-slate-200">{row.metric}</td>
                    <td className="py-1 font-semibold text-slate-900 dark:text-white">{row.segment}</td>
                    <td className="py-1 text-slate-700 dark:text-slate-200">{row.general}</td>
                    <td className="py-1 font-semibold text-slate-900 dark:text-white">{row.delta}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">Karşılaştırma kapalı veya veri yok.</p>
        )}
        <div className="mt-2 flex items-center justify-between text-xs text-slate-500 dark:text-slate-400">
          <span>
            Sayfa {sayfalama.sayfa}/{sayfalama.toplamSayfa} • Toplam sonuç: {formatNum(Math.max(sayfalama.sonucToplam, sayfalama.kiyasToplam))}
          </span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setSayfa((prev) => Math.max(1, prev - 1))}
              disabled={sayfalama.sayfa <= 1}
              className="rounded-md border border-slate-200 px-2 py-1 font-semibold text-slate-600 disabled:opacity-50 dark:border-slate-700 dark:text-slate-300"
            >
              Önceki
            </button>
            <button
              type="button"
              onClick={() => setSayfa((prev) => Math.min(sayfalama.toplamSayfa, prev + 1))}
              disabled={sayfalama.sayfa >= sayfalama.toplamSayfa}
              className="rounded-md border border-slate-200 px-2 py-1 font-semibold text-slate-600 disabled:opacity-50 dark:border-slate-700 dark:text-slate-300"
            >
              Sonraki
            </button>
          </div>
        </div>
      </section>

      <section className="rounded-lg border border-slate-200 bg-white p-3 dark:border-slate-800 dark:bg-slate-900/60">
        <p className="text-xs font-semibold text-slate-500 dark:text-slate-400">Yemek Tipi Dağılımı</p>
        {kitchenBreakdown.length === 0 ? (
          <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">Bu sorgu için kırılım verisi yok.</p>
        ) : (
          <ul className="mt-2 space-y-1.5 text-sm">
            {kitchenBreakdown.map((row) => (
              <li key={row.label} className="flex items-center justify-between">
                <span className="text-slate-600 dark:text-slate-300">{row.label}</span>
                <span className="font-bold text-slate-900 dark:text-white">{row.value}</span>
              </li>
            ))}
          </ul>
        )}
      </section>

      {queryLoading ? (
        <section className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-500 dark:border-slate-700 dark:bg-slate-900/40 dark:text-slate-300">
          Segment analizi sorgusu çalıştırılıyor...
        </section>
      ) : null}
      {queryError ? (
        <section className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700 dark:border-rose-700 dark:bg-rose-900/20 dark:text-rose-300">
          {queryError}
        </section>
      ) : null}

      {guideModalOpen ? (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/60 p-4">
          <div className="max-h-[90vh] w-full max-w-4xl overflow-y-auto rounded-xl border border-slate-200 bg-white p-4 shadow-2xl dark:border-slate-700 dark:bg-slate-900">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h4 className="text-base font-black text-slate-900 dark:text-white">Segment Analizi Sistemi Nasıl Çalışır?</h4>
                <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">
                  Burası kullanıcılarına soru sorduğun yer — bir Excel pivot tablosu gibi. Üç şey seçersin: KİMLER (segment + filtre),
                  NE ÖLÇEYİM (metrik) ve NASIL PARÇALAYAYIM (kırılım); çalıştırınca cevabı tablo olarak alırsın. Burada bir şey yapmazsın,
                  veriyi okursun. "Sistemin sana hazır getirdiği çıkarımlar" için Davranış İçgörüleri sekmesine bak.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setGuideModalOpen(false)}
                className="rounded-md border border-slate-200 px-2 py-1 text-xs font-semibold text-slate-600 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
              >
                Kapat
              </button>
            </div>

            <div className="mt-4 space-y-4 text-sm text-slate-700 dark:text-slate-200">
              <section>
                <h5 className="font-bold text-slate-900 dark:text-white">Akış</h5>
                <ol className="mt-1 list-decimal pl-5 space-y-1">
                  <li>Önce segment ekseni seçilir (ör. Bebekli).</li>
                  <li>Filtre eklenir (ör. Alerjen içerir gluten, Premium eşittir evet).</li>
                  <li>Metrikler seçilir (kabul oranı, plan uyumu, alışverişe dönüşüm).</li>
                  <li>Kırılım seçilir (yemek tipi, alerjen türü, premium/free).</li>
                  <li>Sonuç tablosu ve karşılaştırma okunur; veri kalitesi uyarıları kontrol edilir.</li>
                </ol>
              </section>

              <section>
                <h5 className="font-bold text-slate-900 dark:text-white">Yorumlama Kılavuzu</h5>
                <ul className="mt-1 list-disc pl-5 space-y-1">
                  <li>Kullanıcı sayısı düşükse sonuç “gözlem” seviyesinde ele alınmalı.</li>
                  <li>Gösterim yokken kabul varsa izleme akışı ve olay sırası kontrol edilmeli.</li>
                  <li>Karşılaştırma açıkken segment-genel farkı tek başına karar değildir; örneklemle birlikte okunur.</li>
                </ul>
              </section>

              <section>
                <h5 className="font-bold text-slate-900 dark:text-white">Örnek Sorgular</h5>
                <div className="mt-1 space-y-2">
                  <div className="rounded-lg border border-slate-200 p-2 dark:border-slate-700">
                    <p className="font-semibold">Örnek 1: Bebekli + Alerjen</p>
                    <p>Segment: Bebekli</p>
                    <p>Filtre: Alerjen içerir “gluten”</p>
                    <p>Metrikler: Kullanıcı sayısı, kabul oranı, plan uyumu</p>
                    <p>Kırılım: Yemek tipi</p>
                  </div>
                  <div className="rounded-lg border border-slate-200 p-2 dark:border-slate-700">
                    <p className="font-semibold">Örnek 2: Premium + Plan Oluştu</p>
                    <p>Segment: Premium</p>
                    <p>Filtre: Plan oluştu = Evet</p>
                    <p>Metrikler: Alışverişe dönüşüm, ortalama harcama, wow oranı</p>
                    <p>Kırılım: Premium/Free, Alerjen türü</p>
                  </div>
                  <div className="rounded-lg border border-slate-200 p-2 dark:border-slate-700">
                    <p className="font-semibold">Örnek 3: Yoğun Çalışan + Diyet</p>
                    <p>Segment: Yoğun Çalışan</p>
                    <p>Filtre: Alerjen içerir “laktoz” (veya özel koşul)</p>
                    <p>Metrikler: Kabul oranı, plan uyumu, israf oranı</p>
                    <p>Kırılım: Yemek tipi</p>
                  </div>
                </div>
              </section>
            </div>
          </div>
        </div>
      ) : null}
    </section>
  );
}


